import { HttpRequestMethodTypes } from 'types';
import endpoint, { type ErrorResponse } from 'utils/endpoint';
import { withTryAsync } from 'utils/withTry';
import Listing from './types/listing';
import { sortListingsByDate } from './utils';
import { FILTER_PROPERTY, STATUS_PROPERTY } from 'contexts/preferences/listing-params/types';

import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type { Meta } from 'utils/types';
import type { Cluster } from 'components/search/types';
import type { PartialDeep } from 'type-fest';
import type { ClientAppListing, ListingLanguage } from './types/listing';
import { SiteLanguage } from 'types/locales';

export type GetListingsQuery = Omit<ListingParams, typeof FILTER_PROPERTY> & {
  serializer?: string;
  [FILTER_PROPERTY]: Omit<ListingParams[typeof FILTER_PROPERTY], typeof STATUS_PROPERTY> & {
    [STATUS_PROPERTY]: { available: boolean; notAvailable: boolean; notAvailableSold: boolean; notAvailableOther: boolean; deleted: boolean };
  };
};

/**
 * Fetches listings from the API based on the provided query parameters.
 *
 * This function makes a GET request to the listings API endpoint and returns
 * Listing objects along with  pagination metadata.
 *
 * @param query Query parameters for filtering and pagination.
 *   Common parameters include:
 *   - `page.number`: Page number for pagination
 *   - `page.size`: Number of listings per page
 *   - `filter.*`: Various filter criteria (e.g., price range, location, property type)
 *   - `sort`: Sorting criteria
 * @param abortSignal Optional AbortSignal to cancel the request.
 *
 * @returns A promise that resolves to an object containing:
 *   - `listings`: Array of Listing objects
 *   - `meta`: Pagination metadata including totalPages, totalCount, pageNumber, and pageSize
 *
 * @throws When the API request fails, returns listings as empty array with error metadata
 *
 * @example
 * // Usage
 * const { listings, meta } = await getListings({
 *   page: { number: 1, size: 20 },
 *   filter: { price: { min: 500000, max: 1000000 } }
 * });
 *
 * @example
 * // With abort signal for request cancellation
 * const controller = new AbortController();
 * const { listings, meta } = await getListings({
 *   page: { number: 1, size: 20 }
 * }, controller.signal);
 */
export async function getListings(query: PartialDeep<GetListingsQuery>, abortSignal?: AbortSignal, language: ListingLanguage = SiteLanguage.English): Promise<{ listings: Listing[]; meta: Meta }> {
  const [response, error] = await withTryAsync<{ data: ClientAppListing[]; meta: Meta }, ErrorResponse>(endpoint)('/services/api/v3/listings', HttpRequestMethodTypes.GET, query, undefined, abortSignal);

  if (error) {
    return { listings: [], meta: error.meta as Meta };
  }
  
  const { data, meta } = response;

  if (!data || !Array.isArray(data)) {
    return { listings: [], meta: meta || { totalPages: 0, totalCount: 0, pageNumber: 0, pageSize: 0 } };
  }

  const listings = data.map(d => Listing.fromClientAppListing(d, language));
  return { listings, meta };
}

/**
 * @deprecated
 *
 * @see SearchPageModel#getClustersWithinBoundary
 * @see SearchPageModel#getListingsWithinBoundary
 */
export async function getClustersAndListings(params: Record<string, unknown> = {}, isServerSide = false) {
  const { data, meta } = await endpoint<{ data: Record<string, unknown>[]; meta: Meta }>('/services/api/v3/listings', HttpRequestMethodTypes.GET, params);
  const filteredListings = data.filter(data => data.type === 'listings') as ClientAppListing[];
  const clusters = data.filter(data => data.type === 'clusters') as unknown as Cluster[];

  if (isServerSide) {
    return { listings: filteredListings, clusters, meta };
  }
  const listings = filteredListings.map(d => Listing.fromClientAppListing(d));
  return { listings, clusters, meta: meta };
}

export type GetListingsBySlugParams = {
  slug: string;
  getDeleted?: boolean;
  serializer?: string;
};

/**
 * Fetches listings by slug with configurable status filters and sorting.
 *
 * This function retrieves listings based on a location slug (e.g., "toronto-on") and allows
 * filtering by various listing statuses including available, sold, expired, and deleted listings.
 * The results are automatically sorted by date in descending order.
 *
 * @param params The parameters for the listings query.
 *   - `slug`: The location slug to search for (e.g., "toronto-on", "vancouver-bc")
 *   - `getDeleted`: Whether to include deleted listings in the results. Defaults to false.
 *   - `serializer`: The serializer format for the API response. Defaults to 'full'.
 * @param abortSignal Optional {@link AbortSignal} to cancel the request.
 *
 * @returns A promise that resolves to an object containing:
 *   - `listings`: Array of {@link Listing} objects sorted by date (newest first)
 *   - `meta`: Pagination metadata including totalPages, totalCount, pageNumber, and pageSize
 *
 * @throws When the API request fails, returns listings as empty array with error metadata
 *
 * @example
 * // Fetch active listings for Toronto
 * const { listings, meta } = await getListingsBySlug({
 *   slug: 'toronto-on',
 *   getDeleted: false
 * });
 *
 * @example
 * // Fetch all listings including deleted ones
 * const { listings, meta } = await getListingsBySlug({
 *   slug: 'vancouver-bc',
 *   getDeleted: true,
 *   serializer: 'minimal'
 * });
 *
 * @example
 * // With abort signal for request cancellation
 * const controller = new AbortController();
 * const { listings, meta } = await getListingsBySlug({
 *   slug: 'montreal-qc'
 * }, controller.signal);
 */
export async function getListingsBySlug(params: GetListingsBySlugParams, abortSignal?: AbortSignal, language: ListingLanguage = SiteLanguage.English) {
  const { slug, getDeleted = false, serializer = 'full' } = params;
  
  // Fetch non-pending listings using status filters
  const nonPendingParams: PartialDeep<GetListingsQuery> = {
    [FILTER_PROPERTY]: {
      slug,
      [STATUS_PROPERTY]: {
        available: true,
        notAvailable: true,
        notAvailableSold: true,
        notAvailableOther: true,
        deleted: getDeleted,
      },
    },
    serializer,
  };
  
  // Fetch pending listings using standard_status (backend requires this instead of status filters)
  const pendingParams: PartialDeep<GetListingsQuery> & {
    [FILTER_PROPERTY]: PartialDeep<GetListingsQuery[typeof FILTER_PROPERTY]> & { standard_status?: string };
  } = {
    [FILTER_PROPERTY]: {
      slug,
      ...(getDeleted ? { [STATUS_PROPERTY]: { deleted: getDeleted } } : {}),
      standard_status: 'Pending',
    },
    serializer,
  };
  
  // Fetch both sets of listings in parallel
  const [nonPendingResult, pendingResult] = await Promise.all([
    getListings(nonPendingParams, abortSignal, language),
    getListings(pendingParams, abortSignal, language),
  ]);
  
  // Combine listings and deduplicate by ID
  const allListings = [...nonPendingResult.listings, ...pendingResult.listings];
  const uniqueListings = Array.from(
    new Map(allListings.map(listing => [listing.id, listing])).values()
  );
  
  // Combine meta information
  const combinedMeta = {
    totalPages: Math.max(nonPendingResult.meta.totalPages || 0, pendingResult.meta.totalPages || 0),
    totalCount: (nonPendingResult.meta.totalCount || 0) + (pendingResult.meta.totalCount || 0),
    pageNumber: nonPendingResult.meta.pageNumber || 1,
    pageSize: nonPendingResult.meta.pageSize || pendingResult.meta.pageSize || 30,
  };
  
  return { listings: sortListingsByDate(uniqueListings), meta: combinedMeta };
}

/**
 * Fetches a single listing by its unique identifier.
 *
 * This function retrieves a specific listing from the API using its ID.
 *
 * @param id The unique identifier of the listing (can be string or number)
 * @param abortSignal Optional {@link AbortSignal} to cancel the request.
 *
 * @returns A promise that resolves to:
 *   - A {@link Listing} object instance
 *   - `undefined` if the listing is not found or the request fails
 *
 * @throws When the API request fails, returns `undefined` instead of throwing
 *
 * @example
 * // Fetch a listing by ID (client-side)
 * const listing = await getListingById('12345');
 * if (listing) {
 *   console.log(listing.price);
 * }
 *
 * @example
 * // With abort signal for request cancellation
 * const controller = new AbortController();
 * const listing = await getListingById('12345', controller.signal);
 */
export async function getListingById(id: string | number, abortSignal?: AbortSignal) {
  const [response, error] = await withTryAsync<{ data: ClientAppListing }, ErrorResponse>(endpoint)(`/services/api/v3/listings/${id}`, HttpRequestMethodTypes.GET, undefined, undefined, abortSignal);

  if (error) {
    return undefined;
  }
  const { data } = response;
  return data ? Listing.fromClientAppListing(data) : undefined;
}